<script setup lang="ts">
import dayjs from "dayjs";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import { usePatientsPaginatedFiltered } from "@/composables/models/patient/usePatient";
import { EPatientType } from "@/api/models/patient/patient";
import ChatDockTabBase from "@/components/models/message/chatDock/tabs/ChatDockTabBase.vue";
import type { ChatDockEmitPayload } from "@/types/general";
import { formatName } from "@/utils/Formatters.ts";
import { Tippy } from "vue-tippy";

defineProps<{
  active: boolean;
  collapsed: boolean;
  search: string;
  selectedId: string;
}>();

const emit = defineEmits<{
  (e: "select", payload: ChatDockEmitPayload<"patients">): void;
  (e: "update:loading", value: boolean): void;
}>();
</script>

<template>
  <chat-dock-tab-base
    kind="patients"
    :active="active"
    :collapsed="collapsed"
    :search="search"
    :use-list="usePatientsPaginatedFiltered"
    :build-filters="(s) => ({ search: s, page_size: 25, type: EPatientType.PATIENT })"
    :select-items="(data) => data?.pages?.flatMap((p: any) => p.results) ?? []"
    :select-id="(p: any) => p.id"
    @select="emit('select', $event as any)"
    @update:loading="emit('update:loading', $event)"
  >
    <template #collapsed="{ item, select }">
      <tippy placement="right">
        <v-btn
          icon
          variant="text"
          class="tw:!min-w-10 tw:!h-10 tw:!w-10 tw:!rounded-full tw:!overflow-visible"
          :class="item.id === selectedId ? 'tw:ring-3 tw:ring-secondary' : ''"
          @click="select"
        >
          <user-avatar
            :user="item"
            size="36"
            rounded="full"
            hide-badges
            no-image
          />
        </v-btn>

        <template #content>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:font-semibold">{{ formatName(item) }}</span>
            <span class="tw:text-xs tw:text-slate-200">#{{ item.hrid }}</span>
          </div>
        </template>
      </tippy>
    </template>

    <template #expanded="{ item, select }">
      <v-list-item
        variant="text"
        class="tw:!my-0.5 tw:!rounded-xl tw:!bg-transparent tw:!text-current hover:tw:!bg-current/10"
        rounded="md"
        active-class="tw:!bg-current/15 tw:!ring-2 tw:!ring-inset tw:!ring-current/30"
        @click="select"
        :active="item.id === selectedId"
      >
        <template #prepend>
          <user-avatar
            :user="item"
            size="40"
            rounded="circle"
            class="tw:!mr-2"
            hide-badges
          />
        </template>

        <template #title>
          <span class="tw:text-ms tw:font-bold">{{ item.first_name }} {{ item.last_name }}</span>
        </template>

        <template #subtitle>
          <span class="tw:text-xxs tw:font-semibold tw:text-current/70">
            #{{ item.hrid }} • {{ item.birth_date ? dayjs().diff(item.birth_date, "year") : "-" }}y
          </span>
        </template>
      </v-list-item>
    </template>
  </chat-dock-tab-base>
</template>
